import { CloseOutlined } from "@ant-design/icons";
import { Button, Modal, Progress, Spin, Typography } from "antd";
import Cookies from "js-cookie";
import { useEffect, useRef, useState } from "react";
import apiFactory from "../../api";
import { uploadMultipartFile } from "../../api/authentication/storageApi";
import { useMenuContext } from "../../context/MenuContext";
import { isImageFile } from "../../utils/Utils";
import { compressImage } from "../../utils/SendFile";
import "./style.scss";

const PreviewFileUploadModal = ({
  isModalOpen,
  handleCancel,
  uploadFiles,
  setUploadFiles,
  setPreviewFiles,
}) => {
  const { selectedConversation } = useMenuContext();
  const [abortController, setAbortController] = useState({});
  const sharedAbortController = useRef(null);
  const fileUploading = useRef(null);
  const [progressState, setProgressState] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);

  const sendFile = async () => {
    const controller = new AbortController();
    sharedAbortController.current = controller;

    try {
      for (const file of uploadFiles) {
        fileUploading.current = file;

        const rs = await handleSendFileV2(file);

        if (controller.signal.aborted) {
          throw new Error("Aborted");
        }

        if (rs) {
          const fileConvert = {
            uid: file?.uid,
            name: file?.name,
            originalName: file?.name,
            mainPath: rs?.location,
            thumbnailPreviewPath:
              rs?.thumbnailPreviewPath ||
              rs?.videoImgPreviewPath ||
              rs?.location,
            videoImgPreviewPath: rs?.videoImgPreviewPath,
            width: rs?.videoImgPreviewWidth || rs?.width,
            height: rs?.videoImgPreviewHeight || rs?.height,
            duration: rs?.videoDuration || rs?.duration,
            volume: rs?.fileSize,
            contentType: rs?.contentType,
            status: "done",
          };

          setPreviewFiles((prev) => [...(prev || []), fileConvert]);
        }

        fileUploading.current = null;
      }
    } catch (error) {}
  };

  const handleDeleteFile = (uid) => {
    const fileToDelete = uploadFiles?.find((file) => file?.uid === uid);

    if (fileToDelete && abortController) {
      abortController.abort();

      const newUploadFilesV2 = uploadFiles?.filter((file) => file?.uid !== uid);
      setUploadFiles([...(newUploadFilesV2 || [])]);

      apiFactory.messageApi.cancelMessage({
        requestUuid: uid,
      });
    }
  };

  const handleSendFileV2 = async (file) => {
    const controller = new AbortController();
    setAbortController(controller);

    const reqUuid = file?.uid;
    const uploadFile = uploadFiles?.find((file) => file?.uid === reqUuid);
    if (!uploadFile) return false;

    try {
      const resource = await uploadMultipartFile({
        file: uploadFile,
        bucket: "notification",
        signal: controller.signal,
        onProgress: ({ percent }) => {
          updateProgress(file, percent);
        },
      });

      // const payload = {
      //   conversationId: selectedConversation?.conversationId || null,
      //   requestUuid: reqUuid,
      //   contentType: MESSAGE_STATUS.FILE,
      //   name: uploadFile?.name,
      //   token: Cookies.get("access_token"),
      //   totalVolume: uploadFile?.size,
      //   totalChunk: 1,
      //   actualContentType: uploadFile?.type,
      //   resource: resource,
      //   folder: "NOTIFICATION",
      // };
      //
      // const response = await apiFactory.messageApi.sendFile(payload);

      let thumbnailRes = null;
      if (
        isImageFile(uploadFile?.name) ||
        uploadFile?.type?.startsWith("image/")
      ) {
        try {
          const compressedFile = await compressImage(uploadFile);
          if (compressedFile) {
            thumbnailRes = await uploadMultipartFile({
              file: compressedFile,
              bucket: "notification",
              signal: controller.signal,
            });
          }
        } catch (thumbErr) {
          console.error("ERR creating/uploading thumbnail:", thumbErr);
        }
      }

      fileUploading.current.isSuccess = true;
      return {
        ...resource,
        thumbnailPreviewPath:
          thumbnailRes?.location ||
          resource?.videoImgPreviewPath ||
          resource?.location,
      };
    } catch (error) {
      console.error("ERR upload/send File:", error);
    }
  };

  const updateProgress = (file, percent) => {
    setProgressState((prev) => ({
      ...prev,
      [file.uid]: percent,
    }));
  };

  const getFileSize = (file) => {
    if (!file?.size) return "0 KB";

    const sizeInMB = file.size / (1024 * 1024);
    const sizeInKB = file.size / 1024;

    return sizeInMB >= 1
      ? `${sizeInMB.toFixed(1)} MB`
      : `${sizeInKB.toFixed(1)} KB`;
  };

  const handleCloseModal = () => {
    handleStopAllUploads();
    handleCancel();
    setUploadFiles([]);
    setProgressState({});
  };

  const handleStopAllUploads = () => {
    if (sharedAbortController.current && abortController) {
      abortController.abort();
      sharedAbortController.current.abort();
    }

    setUploadFiles([]);
  };

  useEffect(() => {
    if (uploadFiles?.length > 0 && !isUploading) {
      setIsLoading(false);
      setIsUploading(true);
      sendFile().finally(() => {
        setIsUploading(false);

        setTimeout(() => {
          handleCancel();
        }, 1000);
      });
    }
  }, [uploadFiles?.length]);

  return (
    <Modal
      title="Upload"
      open={isModalOpen}
      centered
      closable={false}
      maskClosable={false}
      footer={
        <div style={{ marginTop: 20, textAlign: "right" }}>
          <Button onClick={handleCloseModal}>Cancel</Button>
        </div>
      }
    >
      <Typography.Title level={5}>Attach attachments</Typography.Title>
      {!isLoading ? (
        uploadFiles?.map((file) => (
          <div key={file?.uid}>
            <div style={{ marginTop: 20 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginTop: 10,
                }}
              >
                <Typography.Text>{file?.name}</Typography.Text>
                {fileUploading.current?.uid === file?.uid && (
                  <CloseOutlined
                    style={{ marginLeft: 10, cursor: "pointer" }}
                    onClick={() => handleDeleteFile(file?.uid)}
                  />
                )}
                <Typography.Text style={{ marginLeft: "auto" }}>
                  {getFileSize(file)}
                </Typography.Text>
              </div>

              <Progress percent={progressState[file?.uid] || 0} />
            </div>
          </div>
        ))
      ) : (
        <div className="w-full h-full items-center justify-center pt-5 pb-5">
          <Spin />
        </div>
      )}
    </Modal>
  );
};

export { PreviewFileUploadModal };
